import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home } from 'lucide-react';
import { ProfileDropdown } from './ProfileDropdown';
import { NotificationBell } from '../../../features/notifications';
import { Logo } from '../ui';

/**
 * Header component with navigation and user menu
 * What: Responsive header with left-aligned logo and navigation, right-aligned user actions
 * Why: Follows best UX for dashboard layouts, improves discoverability of main nav
 * Where: shared/components/layout/Header.js
 */
export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const mobileButtonRef = useRef(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', current: location.pathname === '/dashboard' },
    { name: 'Employees', href: '/employees', current: location.pathname === '/employees' },
    { name: 'Attendance', href: '/attendance', current: location.pathname === '/attendance' },
    { name: 'Sites', href: '/sites', current: location.pathname === '/sites' },
    { name: 'Devices', href: '/devices', current: location.pathname === '/devices' },
    { name: 'System', href: '/system', current: location.pathname === '/system' },
  ];

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 w-full fixed top-0 left-0 right-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Desktop Layout */}
        <div className="hidden lg:flex justify-between items-center h-16">
          {/* Left: Logo */}
          <div className="flex items-center h-16">
            <Logo className="w-28 h-16"/>
          </div>

          {/* Center: Desktop Navigation */}
          <nav className="flex items-center h-16">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center justify-center h-16 min-w-[100px] 
                  text-sm font-medium transition-colors duration-200
                  ${item.current
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-primary-600 hover:text-white'}
                `}
                style={{
                  margin: 0,
                  padding: 0,
                  borderRadius: 0,
                }}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right: Notifications, User */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            <ProfileDropdown />
          </div>
        </div>

        {/* Tablet Layout */}
        <div className="hidden md:flex lg:hidden justify-between items-center h-16">
          {/* Left: Logo */}
          <div className="flex items-center h-16">
            <Logo className="w-24 h-14"/>
          </div>

          {/* Center: Tablet Navigation */}
          <nav className="flex items-center h-16">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center justify-center h-16 min-w-[80px] 
                  text-xs font-medium transition-colors duration-200
                  ${item.current
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-primary-600 hover:text-white'}
                `}
                style={{
                  margin: 0,
                  padding: 0,
                  borderRadius: 0,
                }}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Right: Notifications, User */}
          <div className="flex items-center space-x-2">
            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            <ProfileDropdown />
          </div>
        </div>

        {/* Mobile Layout (top): Logo left, actions right */}
        <div className="md:hidden flex items-center justify-between h-16">
          {/* Left: Logo */}
          <div className="flex items-center h-16">
            <Logo className="w-auto h-14"/>
          </div>
          {/* Right: Notifications, Profile */}
          <div className="flex justify-end items-center space-x-4">
            <NotificationBell />
            <ProfileDropdown />
          </div>
        </div>

        {/* Mobile Bottom Nav */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-[60] bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-t border-gray-200">
          <div className="grid grid-cols-2 py-2">
            <Link to="/dashboard" className="flex flex-col items-center justify-center text-primary-600">
              <Home className="h-6 w-6" />
              <span className="text-xs mt-1">Home</span>
            </Link>
            <button
              ref={mobileButtonRef}
              onClick={() => setIsMobileMenuOpen(true)}
              className="flex flex-col items-center justify-center text-primary-600"
            >
              <Menu className="h-6 w-6" />
              <span className="text-xs mt-1">Menu</span>
            </button>
          </div>
        </div>

        {/* Mobile Side Drawer */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-50 bg-black/40"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Panel */}
            <div
              ref={mobileMenuRef}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl ring-1 ring-black/10 p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-900">Menu</span>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="grid gap-2">
                {navigation.filter(n => n.name !== 'Dashboard').map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`w-full px-3 py-3 rounded-lg border border-gray-200
                      ${item.current
                        ? 'bg-primary-600 text-white font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'}
                    `}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}; 